import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APP_LABELS, type AppKey } from '@cyntox/common/AppShell';
import { api, ConflictError } from '../api/client';
import type { AdminAssignment, AdminRole, AdminUser, CapabilityMatrix, EmployeeRosterRow, RoleDetail } from '../api/types';
import { useApp } from '../state/AppContext';
import { ConflictBanner, EmployeePicker, EmptyRow, ErrorBanner, SortableHeader, useTableSort } from '../components/ui';

/**
 * Who can sign in, and what each person's access is.
 *
 * Access reads the way people think about it: an app, a role in that app, and
 * where it applies. Each role belongs to one app and grants only things in it,
 * so "Beth: Facility Scorecard as Operations Admin, all locations" is the whole
 * story. Someone covering two plants, or two apps, simply has two lines.
 * Lines are added and removed, never edited, so each change stands on its own
 * in the history.
 */

const APPS = Object.keys(APP_LABELS) as AppKey[];
const appLabel = (app: string) => APP_LABELS[app as AppKey] ?? app;

interface AccessLine { app: AppKey; roleKey: string; locationCode: string }

/** What an access line can be scoped to in one app: facilities, or Inventory's regions. */
interface ScopeChoice { label: string; allLabel: string; options: { code: string; name: string }[] }

export function Administration() {
  const { reference, can, me } = useApp();
  const queryClient = useQueryClient();
  const canEdit = can('users', 'edit');
  // The route already requires users:view; this keeps the queries from firing
  // if the screen is ever mounted without that gate.
  const canView = can('users', 'view');

  const [tab, setTab] = useState<'people' | 'roles'>('people');
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [openUpn, setOpenUpn] = useState<string | null>(null);

  const [newUpn, setNewUpn] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmployeeId, setNewEmployeeId] = useState<number | null>(null);
  const [newAccess, setNewAccess] = useState<AccessLine[]>([{ app: 'scorecard', roleKey: '', locationCode: '' }]);
  const [pickerKey, setPickerKey] = useState(0);

  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<{ rows: AdminUser[] }>('/api/admin/users'),
    enabled: canView,
  });
  const roles = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => api.get<AdminRole[]>('/api/admin/roles'),
    enabled: canView,
  });
  const roster = useQuery({
    queryKey: ['admin-roster'],
    queryFn: () => api.get<{ rows: EmployeeRosterRow[] }>('/api/employees', { take: 5000 }),
    // Only the add and manage forms use it, and the API serves it to users:edit.
    enabled: canEdit,
  });

  // Where an access line applies: a facility for the Facility Scorecard and
  // Admin, a region for Inventory — whose locations are trucks and supply units
  // rather than facilities, and whose access is organised by region.
  const scopesFor = (app: string): ScopeChoice => (app === 'inventory'
    ? { label: 'REGION', allLabel: 'All regions', options: reference.inventoryRegions ?? [] }
    : { label: 'LOCATION', allLabel: 'All locations', options: reference.facilities });
  const locationName = (code: string | null | undefined, app: string) => {
    const scope = scopesFor(app);
    return code ? scope.options.find((o) => o.code === code)?.name ?? code : scope.allLabel.toLowerCase();
  };

  const flash = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(null), 5000);
  };
  const handleError = (e: Error) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message));
  // Changing access changes what the signed-in person can see, so nothing is
  // safe to keep cached — including /api/me.
  const refresh = () => queryClient.invalidateQueries();

  const roleList = useMemo(() => roles.data ?? [], [roles.data]);
  const rows = users.data?.rows ?? [];
  const peopleSort = useTableSort(rows, {
    name: (u) => u.displayName,
    upn: (u) => u.userPrincipalName,
    ...Object.fromEntries(APPS.map((app) => [app, (u: AdminUser) =>
      u.assignments.filter((a) => a.app === app).map((a) => a.roleName).join(', ')])),
    status: (u) => (!u.isActive ? 'Inactive' : u.assignments.length === 0 ? 'No access yet' : 'Active'),
  });
  const people = roster.data?.rows ?? [];
  const picked = people.find((p) => p.employeeId === newEmployeeId);
  const chosenLines = newAccess.filter((l) => l.roleKey);

  const addUser = useMutation({
    mutationFn: () =>
      api.post<{ userPrincipalName: string; granted: number }>('/api/admin/users', {
        userPrincipalName: newUpn, displayName: newName || null, employeeId: newEmployeeId,
        access: chosenLines.map((l) => ({ roleKey: l.roleKey, locationCode: l.locationCode || null, title: null })),
      }),
    onSuccess: (r) => {
      const apps = [...new Set(chosenLines.map((l) => appLabel(l.app)))];
      setNewUpn(''); setNewName(''); setNewEmployeeId(null);
      setNewAccess([{ app: 'scorecard', roleKey: '', locationCode: '' }]);
      setPickerKey((k) => k + 1);
      refresh();
      if (r.granted > 0) {
        flash(`${r.userPrincipalName} added with access to ${apps.join(' and ')}.`);
      } else {
        // No access means they see nothing, so open their row: the next thing
        // to do is give them some, and the panel is where that happens.
        setOpenUpn(r.userPrincipalName);
        flash(`${r.userPrincipalName} added with no access yet. Give them access below.`);
      }
    },
    onError: handleError,
  });

  const saveUser = useMutation({
    mutationFn: (u: AdminUser & { nextName: string; nextEmployeeId: number | null; nextActive: boolean }) =>
      api.put(`/api/admin/users/${encodeURIComponent(u.userPrincipalName)}`, {
        displayName: u.nextName || null, employeeId: u.nextEmployeeId,
        isActive: u.nextActive, rowVersionNo: u.rowVersionNo,
      }),
    onSuccess: (_r, u) => { refresh(); flash(`${u.userPrincipalName} updated.`); },
    onError: handleError,
  });

  const removeUser = useMutation({
    mutationFn: (u: AdminUser) => api.del(`/api/admin/users/${encodeURIComponent(u.userPrincipalName)}`),
    onSuccess: (_r, u) => { setOpenUpn(null); refresh(); flash(`${u.userPrincipalName} removed.`); },
    onError: handleError,
  });

  const addAssignment = useMutation({
    mutationFn: (v: { upn: string; roleKey: string; locationCode: string; title: string }) =>
      api.post(`/api/admin/users/${encodeURIComponent(v.upn)}/assignments`, {
        roleKey: v.roleKey, locationCode: v.locationCode || null, title: v.title || null,
      }),
    onSuccess: () => { refresh(); flash('Access added.'); },
    onError: handleError,
  });

  const removeAssignment = useMutation({
    mutationFn: (assignmentId: number) => api.del(`/api/admin/assignments/${assignmentId}`),
    onSuccess: () => { refresh(); flash('Access removed.'); },
    onError: handleError,
  });

  // Picking someone fills in what we already hold about them. Only company
  // addresses are imported from the HR export, so most people have none yet and
  // the login still has to be typed; the hint below says which case this is.
  const chooseEmployee = (employeeId: number | null) => {
    setNewEmployeeId(employeeId);
    const person = people.find((p) => p.employeeId === employeeId);
    if (!person) return;
    setNewName(person.sortName || person.displayName);
    if (person.email) setNewUpn(person.email);
  };


  const columns = 4 + APPS.length;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Administration</div>
          <div className="page-subtitle">
            Who can sign in, and what they can use: for each app, a role and where it applies.
          </div>
        </div>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {conflict && <ConflictBanner message={conflict} onReload={() => { setConflict(null); refresh(); }} />}

      {/* Two jobs live here — who the people are, and what the roles mean.
          Tabs rather than a fourth stacked block, so neither buries the other. */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {(['people', 'roles'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="btn btn--sm"
            style={{
              border: 'none', background: 'none', borderRadius: 0, paddingBottom: 8,
              fontWeight: tab === t ? 700 : 500,
              color: tab === t ? 'var(--blue-deep)' : 'var(--muted)',
              borderBottom: tab === t ? '2px solid var(--blue)' : '2px solid transparent',
            }}
          >
            {t === 'people' ? 'People' : 'Roles'}
          </button>
        ))}
      </div>

      {tab === 'people' && (
        <>
          {canEdit && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="section-label" style={{ marginBottom: 10 }}>ADD A PERSON</div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 300 }}>
                  <label className="field-label" htmlFor="adm-employee">FIND THEM IN THE EMPLOYEE LIST</label>
                  <EmployeePicker
                    key={pickerKey}
                    id="adm-employee"
                    people={people}
                    value={newEmployeeId}
                    onChange={chooseEmployee}
                  />
                </div>
                <div style={{ minWidth: 250 }}>
                  <label className="field-label" htmlFor="adm-upn">WORK EMAIL (SIGN-IN)</label>
                  <input
                    id="adm-upn"
                    className="control"
                    style={{ width: '100%' }}
                    placeholder="name@cyntox.com"
                    value={newUpn}
                    onChange={(e) => setNewUpn(e.target.value)}
                  />
                </div>
                <div style={{ minWidth: 175 }}>
                  <label className="field-label" htmlFor="adm-name">NAME</label>
                  <input
                    id="adm-name"
                    className="control"
                    style={{ width: '100%' }}
                    placeholder="Ricciardi, Marsha"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>
              </div>
              <div className="meta" style={{ marginTop: 8 }}>
                {picked
                  ? picked.email
                    ? `${picked.displayName}'s work address came from the employee list.`
                    : `${picked.displayName} has no company address in the HR export yet, so type the one they sign in with. Personal addresses are never imported.`
                  : 'Find someone in the employee list to fill in their name and work address, or type a login directly.'}
              </div>

              <div className="section-label" style={{ margin: '16px 0 6px' }}>ACCESS</div>
              <AccessLines
                lines={newAccess}
                roles={roleList}
                scopesFor={scopesFor}
                onChange={setNewAccess}
              />
              <div className="meta" style={{ marginTop: 6 }}>
                One line per app. Leave the role empty to add them with no access yet.
              </div>

              <button
                className="btn btn--primary"
                style={{ marginTop: 14 }}
                disabled={!newUpn.trim() || addUser.isPending}
                onClick={() => { setError(null); addUser.mutate(); }}
              >
                {addUser.isPending ? 'Adding…' : 'Add person'}
              </button>
            </div>
          )}

          <div className="section-label" style={{ marginBottom: 2 }}>PEOPLE</div>
          <div className="meta" style={{ marginBottom: 10 }}>
            {rows.length} {rows.length === 1 ? 'person' : 'people'} · {rows.filter((r) => r.isActive).length} active
          </div>
          {notice && <div className="pill pill--good" style={{ display: 'inline-block', marginBottom: 10 }}>{notice}</div>}

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortableHeader label="NAME" column="name" sort={peopleSort.sort} onSort={peopleSort.onSort} />
                  <SortableHeader label="SIGN-IN" column="upn" sort={peopleSort.sort} onSort={peopleSort.onSort} />
                  {APPS.map((app) => (
                    <SortableHeader key={app} label={APP_LABELS[app].toUpperCase()} column={app}
                      sort={peopleSort.sort} onSort={peopleSort.onSort} />
                  ))}
                  <SortableHeader label="STATUS" column="status" sort={peopleSort.sort} onSort={peopleSort.onSort} />
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {users.isLoading && <EmptyRow colSpan={columns}>Loading…</EmptyRow>}
                {users.isError && <EmptyRow colSpan={columns}>{(users.error as Error).message}</EmptyRow>}
                {!users.isLoading && !users.isError && rows.length === 0 && (
                  <EmptyRow colSpan={columns}>Nobody is set up yet.</EmptyRow>
                )}
                {peopleSort.rows.map((u) => (
                  <UserRow
                    // The version in the key re-seeds the panel's edits after a
                    // reload, so a save after a conflict carries the new values.
                    key={`${u.userPrincipalName}-${u.rowVersionNo}`}
                    user={u}
                    columns={columns}
                    roles={roleList}
                    scopesFor={scopesFor}
                    locationName={locationName}
                    people={people}
                    canEdit={canEdit}
                    open={openUpn === u.userPrincipalName}
                    onToggle={() => setOpenUpn(openUpn === u.userPrincipalName ? null : u.userPrincipalName)}
                    onSave={(nextName, nextEmployeeId, nextActive) =>
                      saveUser.mutate({ ...u, nextName, nextEmployeeId, nextActive })}
                    onRemove={() => {
                      if (window.confirm(
                        `Remove ${u.userPrincipalName}?\n\nThey lose access immediately, in every app. `
                        + 'To keep the record, set them inactive instead.')) removeUser.mutate(u);
                    }}
                    onAddAssignment={(roleKey, locationCode, title) =>
                      addAssignment.mutate({ upn: u.userPrincipalName, roleKey, locationCode, title })}
                    onRemoveAssignment={(a) => {
                      const mine = u.userPrincipalName.toLowerCase() === me.upn.toLowerCase();
                      const what = `${a.roleName} in ${appLabel(a.app)} at ${locationName(a.locationCode, a.app)}`;
                      const message = mine
                        ? `Remove your own access: ${what}?\n\nYou lose what it grants as soon as the page reloads, `
                          + 'and you may not be able to give it back.'
                        : `Remove ${what} from ${u.displayName ?? u.userPrincipalName}?`;
                      if (window.confirm(message)) removeAssignment.mutate(a.assignmentId);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'roles' && <RolesTab canEdit={canEdit} onError={handleError} />}
    </div>
  );
}

/**
 * The app → role → location lines used when adding a person. The role list
 * follows the app chosen on its line, so it only ever offers roles that mean
 * something there.
 */
function AccessLines({
  lines, roles, scopesFor, onChange,
}: {
  lines: AccessLine[];
  roles: AdminRole[];
  scopesFor: (app: string) => ScopeChoice;
  onChange: (lines: AccessLine[]) => void;
}) {
  const update = (i: number, patch: Partial<AccessLine>) =>
    onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const unusedApp = APPS.find((app) => !lines.some((l) => l.app === app));

  return (
    <div>
      {lines.map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ minWidth: 180 }}>
            {i === 0 && <label className="field-label">APP</label>}
            <select
              className="control"
              style={{ width: '100%' }}
              value={line.app}
              aria-label="App"
              // A different app means a different set of roles.
              onChange={(e) => update(i, { app: e.target.value as AppKey, roleKey: '', locationCode: '' })}
            >
              {APPS.map((app) => <option key={app} value={app}>{APP_LABELS[app]}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 190 }}>
            {i === 0 && <label className="field-label">ROLE</label>}
            <select
              className="control"
              style={{ width: '100%' }}
              value={line.roleKey}
              aria-label="Role"
              onChange={(e) => update(i, { roleKey: e.target.value })}
            >
              <option value="">Choose a role…</option>
              {roles.filter((r) => r.app === line.app).map((r) => (
                <option key={r.roleKey} value={r.roleKey}>{r.displayName}</option>
              ))}
            </select>
          </div>
          <div style={{ minWidth: 180 }}>
            {i === 0 && <label className="field-label">WHERE</label>}
            <select
              className="control"
              style={{ width: '100%' }}
              value={line.locationCode}
              aria-label={scopesFor(line.app).label === 'REGION' ? 'Region' : 'Location'}
              onChange={(e) => update(i, { locationCode: e.target.value })}
            >
              <option value="">{scopesFor(line.app).allLabel}</option>
              {scopesFor(line.app).options.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
            </select>
          </div>
          {lines.length > 1 && (
            <button className="btn btn--sm" aria-label="Remove this line"
              onClick={() => onChange(lines.filter((_, j) => j !== i))}>
              ✕
            </button>
          )}
        </div>
      ))}
      <button
        className="btn btn--sm"
        onClick={() => onChange([...lines, { app: unusedApp ?? 'scorecard', roleKey: '', locationCode: '' }])}
      >
        + Add access line
      </button>
    </div>
  );
}

/**
 * What each role means. One role at a time rather than a grid: the question
 * people actually ask is "what can an Operations Admin do?", not "who can
 * approve?". Roles are listed under their app, and a role only offers the
 * capabilities of its own app.
 *
 * The list of capabilities comes from the API, because it is the list of checks
 * the code actually makes. A tick that saved but changed nothing would be worse
 * than a missing one.
 */
function RolesTab({ canEdit, onError }: { canEdit: boolean; onError: (e: Error) => void }) {
  const { me } = useApp();
  const queryClient = useQueryClient();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newApp, setNewApp] = useState<AppKey>('scorecard');
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // The API's rule, checked here so the button says why before a round trip does.
  const keyValid = /^[a-z][a-z0-9]{1,23}$/.test(newKey);

  const matrix = useQuery({
    queryKey: ['admin-capabilities'],
    queryFn: () => api.get<CapabilityMatrix>('/api/admin/capabilities'),
  });

  // Changing a role changes what the signed-in person may see, so nothing is
  // safe to keep cached — including /api/me.
  const refresh = () => queryClient.invalidateQueries();

  const toggle = useMutation({
    mutationFn: (v: { roleKey: string; resource: string; permission: string; allowed: boolean }) =>
      api.put('/api/admin/capabilities', v),
    onSuccess: () => refresh(),
    onError,
  });

  const saveRole = useMutation({
    mutationFn: (v: { role: RoleDetail; displayName: string; description: string; isActive: boolean }) =>
      api.put(`/api/admin/roles/${encodeURIComponent(v.role.roleKey)}`, {
        displayName: v.displayName,
        description: v.description || null,
        sortOrder: v.role.sortOrder,
        isActive: v.isActive,
        rowVersionNo: v.role.rowVersionNo,
      }),
    onSuccess: () => refresh(),
    onError,
  });

  const createRole = useMutation({
    mutationFn: () =>
      api.post<{ roleKey: string }>('/api/admin/roles', {
        roleKey: newKey, displayName: newName, description: newDescription || null, app: newApp,
      }),
    onSuccess: (r) => {
      setCreating(false); setNewKey(''); setNewName(''); setNewDescription('');
      setSelectedKey(r.roleKey);
      refresh();
    },
    onError,
  });

  const roles = matrix.data?.roles ?? [];
  const catalogue = matrix.data?.catalogue ?? [];
  const granted = useMemo(() => new Set(matrix.data?.granted ?? []), [matrix.data]);
  const selected = roles.find((r) => r.roleKey === selectedKey) ?? roles[0];
  const mine = selected ? me.assignments.some((a) => a.roleKey === selected.roleKey) : false;

  // Only the selected role's app, in catalogue order — which follows that app's menu.
  const groups = useMemo(() => {
    const out: { group: string; entries: typeof catalogue }[] = [];
    for (const entry of catalogue.filter((e) => e.app === selected?.app)) {
      const last = out[out.length - 1];
      if (last && last.group === entry.group) last.entries.push(entry);
      else out.push({ group: entry.group, entries: [entry] });
    }
    return out;
  }, [catalogue, selected?.app]);

  if (matrix.isLoading) return <div className="spinner-note">Loading roles…</div>;
  if (matrix.isError) return <ErrorBanner message={(matrix.error as Error).message} />;

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 240 }}>
        {APPS.map((app) => (
          <div key={app} style={{ marginBottom: 12 }}>
            <div className="section-label" style={{ marginBottom: 8 }}>{APP_LABELS[app].toUpperCase()} ROLES</div>
            {roles.filter((r) => r.app === app).map((r) => (
              <button
                key={r.roleKey}
                onClick={() => { setSelectedKey(r.roleKey); setCreating(false); }}
                className="btn btn--sm"
                style={{
                  display: 'block', width: '100%', textAlign: 'left', marginBottom: 4,
                  background: selected?.roleKey === r.roleKey && !creating ? 'var(--blue-tint)' : 'none',
                  color: r.isActive ? undefined : 'var(--muted)',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ fontWeight: 600 }}>{r.displayName}{r.isActive ? '' : ' (inactive)'}</div>
                <div className="meta">
                  {r.peopleCount} {r.peopleCount === 1 ? 'person' : 'people'}
                </div>
              </button>
            ))}
          </div>
        ))}
        {canEdit && (
          <button className="btn btn--sm" style={{ width: '100%', marginTop: 4 }}
            onClick={() => { setCreating(true); }}>
            + New role
          </button>
        )}
      </div>

      {creating && canEdit && (
        <div className="card" style={{ minWidth: 380, flex: 1 }}>
          <div className="section-label" style={{ marginBottom: 10 }}>NEW ROLE</div>
          <label className="field-label" htmlFor="role-app">APP</label>
          <select id="role-app" className="control" style={{ width: '100%', marginBottom: 8 }}
            value={newApp} onChange={(e) => setNewApp(e.target.value as AppKey)}>
            {APPS.map((app) => <option key={app} value={app}>{APP_LABELS[app]}</option>)}
          </select>

          <label className="field-label" htmlFor="role-name">NAME</label>
          <input id="role-name" className="control" style={{ width: '100%', marginBottom: 8 }}
            placeholder="Shift Lead" value={newName} onChange={(e) => setNewName(e.target.value)} />

          <label className="field-label" htmlFor="role-key">KEY</label>
          <input id="role-key" className="control" style={{ width: '100%' }}
            placeholder="shiftlead" value={newKey}
            onChange={(e) => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))} />
          <div className="meta" style={{ marginTop: 4, marginBottom: 8, color: newKey && !keyValid ? 'var(--red)' : undefined }}>
            2 to 24 letters and digits, starting with a letter, and it never changes. Nobody sees it — the name
            above is what people read.
          </div>

          <label className="field-label" htmlFor="role-desc">DESCRIPTION</label>
          <input id="role-desc" className="control" style={{ width: '100%', marginBottom: 10 }}
            placeholder="What this role is for" value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)} />

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--primary btn--sm"
              disabled={!newName.trim() || !keyValid || createRole.isPending}
              onClick={() => createRole.mutate()}>
              {createRole.isPending ? 'Creating…' : 'Create role'}
            </button>
            <button className="btn btn--sm" onClick={() => setCreating(false)}>Cancel</button>
          </div>
          <div className="meta" style={{ marginTop: 8 }}>
            A new role grants nothing until you tick what it can do, and it can only grant things in {APP_LABELS[newApp]}.
          </div>
        </div>
      )}

      {!creating && selected && (
        <div style={{ minWidth: 420, flex: 1 }}>
          <RoleDetailPanel
            key={`${selected.roleKey}-${selected.rowVersionNo}`}
            role={selected}
            canEdit={canEdit}
            heldByYou={mine}
            groups={groups}
            granted={granted}
            onToggle={(resource, permission, allowed) =>
              toggle.mutate({ roleKey: selected.roleKey, resource, permission, allowed })}
            onSave={(displayName, description, isActive) =>
              saveRole.mutate({ role: selected, displayName, description, isActive })}
          />
        </div>
      )}
    </div>
  );
}

function RoleDetailPanel({
  role, canEdit, heldByYou, groups, granted, onToggle, onSave,
}: {
  role: RoleDetail;
  canEdit: boolean;
  heldByYou: boolean;
  groups: { group: string; entries: { resource: string; permission: string; label: string; detail: string }[] }[];
  granted: Set<string>;
  onToggle: (resource: string, permission: string, allowed: boolean) => void;
  onSave: (displayName: string, description: string, isActive: boolean) => void;
}) {
  const [name, setName] = useState(role.displayName);
  const [description, setDescription] = useState(role.description ?? '');
  const [active, setActive] = useState(role.isActive);

  const dirty = name !== role.displayName
    || description !== (role.description ?? '')
    || active !== role.isActive;

  return (
    <div className="card">
      <div className="meta" style={{ marginBottom: 8 }}>
        {/^[aeiou]/i.test(appLabel(role.app)) ? 'An' : 'A'} <strong>{appLabel(role.app)}</strong> role — it can only
        grant things in {appLabel(role.app)}.
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 220, flex: 1 }}>
          <label className="field-label" htmlFor={`rn-${role.roleKey}`}>NAME</label>
          <input id={`rn-${role.roleKey}`} className="control" style={{ width: '100%' }}
            value={name} disabled={!canEdit} onChange={(e) => setName(e.target.value)} />
        </div>
        <div style={{ minWidth: 260, flex: 2 }}>
          <label className="field-label" htmlFor={`rd-${role.roleKey}`}>DESCRIPTION</label>
          <input id={`rd-${role.roleKey}`} className="control" style={{ width: '100%' }}
            value={description} disabled={!canEdit} onChange={(e) => setDescription(e.target.value)} />
        </div>
        {canEdit && (
          <button className="btn btn--primary btn--sm" disabled={!dirty}
            onClick={() => onSave(name, description, active)}>
            Save
          </button>
        )}
      </div>

      <div className="meta" style={{ marginTop: 8 }}>
        Held by {role.peopleCount} {role.peopleCount === 1 ? 'person' : 'people'}
        {role.assignmentCount !== role.peopleCount ? ` across ${role.assignmentCount} assignments` : ''}.
        {heldByYou ? ' You hold this role — changes below affect what you can do.' : ''}
      </div>

      {canEdit && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginTop: 8 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active — an inactive role grants nothing to anyone holding it
        </label>
      )}

      <div className="section-label" style={{ margin: '16px 0 4px' }}>WHAT THIS ROLE CAN DO</div>
      <div className="meta" style={{ marginBottom: 10 }}>
        Each change saves on its own, and takes effect the next time that person loads a page.
      </div>

      {groups.map((g) => (
        <div key={g.group} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{g.group}</div>
          {g.entries.map((e) => {
            const on = granted.has(`${role.roleKey}|${e.resource}|${e.permission}`);
            return (
              <label key={`${e.resource}|${e.permission}`}
                style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '3px 0' }}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!canEdit}
                  style={{ marginTop: 3 }}
                  onChange={(ev) => onToggle(e.resource, e.permission, ev.target.checked)}
                />
                <span style={{ fontSize: 12.5 }}>
                  {e.label}
                  <span className="meta" style={{ display: 'block' }}>{e.detail}</span>
                </span>
              </label>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function UserRow({
  user, columns, roles, scopesFor, locationName, people, canEdit, open,
  onToggle, onSave, onRemove, onAddAssignment, onRemoveAssignment,
}: {
  user: AdminUser;
  columns: number;
  roles: AdminRole[];
  scopesFor: (app: string) => ScopeChoice;
  locationName: (code: string | null | undefined, app: string) => string;
  people: EmployeeRosterRow[];
  canEdit: boolean;
  open: boolean;
  onToggle: () => void;
  onSave: (name: string, employeeId: number | null, active: boolean) => void;
  onRemove: () => void;
  onAddAssignment: (roleKey: string, locationCode: string, title: string) => void;
  onRemoveAssignment: (assignment: AdminAssignment) => void;
}) {
  const [name, setName] = useState(user.displayName ?? '');
  const [employeeId, setEmployeeId] = useState<number | null>(user.employeeId ?? null);
  // Typed text that names nobody yet. Saving then would send "no employee" and
  // silently unlink them, so Save waits until it resolves or is cleared.
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(user.isActive);
  const [line, setLine] = useState<AccessLine & { title: string }>({ app: 'scorecard', roleKey: '', locationCode: '', title: '' });

  const inApp = (app: string) => user.assignments.filter((a) => a.app === app);
  const describe = (a: AdminAssignment) =>
    `${a.roleName} · ${locationName(a.locationCode, a.app)}${a.title ? ` · ${a.title}` : ''}`;

  return (
    <>
      <tr style={!user.isActive ? { color: 'var(--muted)' } : undefined}>
        <td className="strong">
          {user.displayName ?? '—'}
          {user.employeeName && <div className="meta">{user.employeeName} ({user.employeeId})</div>}
        </td>
        <td>{user.userPrincipalName}</td>
        {APPS.map((app) => (
          <td key={app}>
            {inApp(app).length === 0
              ? <span className="meta">—</span>
              : inApp(app).map((a) => (
                  <span key={a.assignmentId} className="pill"
                    style={{ background: 'var(--blue-tint)', color: 'var(--blue-deep)', marginRight: 4, marginBottom: 2, display: 'inline-block' }}>
                    {describe(a)}
                  </span>
                ))}
          </td>
        ))}
        <td>
          {!user.isActive ? 'Inactive'
            : user.assignments.length === 0
              ? <span className="pill" style={{ background: 'var(--orange-tint)', color: 'var(--orange-deep)' }}>No access yet</span>
              : 'Active'}
        </td>
        <td className="num" style={{ whiteSpace: 'nowrap' }}>
          {canEdit && <button className="btn btn--sm" onClick={onToggle}>{open ? 'Close' : 'Manage'}</button>}
        </td>
      </tr>

      {open && canEdit && (
        <tr>
          <td colSpan={columns} style={{ background: 'var(--surface-muted)' }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: '4px 0 10px' }}>
              <div style={{ minWidth: 300 }}>
                <div className="section-label" style={{ marginBottom: 8 }}>DETAILS</div>
                <label className="field-label" htmlFor={`n-${user.userPrincipalName}`}>NAME</label>
                <input id={`n-${user.userPrincipalName}`} className="control" style={{ width: '100%', marginBottom: 8 }}
                  value={name} onChange={(e) => setName(e.target.value)} />

                <label className="field-label" htmlFor={`e-${user.userPrincipalName}`}>EMPLOYEE RECORD</label>
                <EmployeePicker
                  id={`e-${user.userPrincipalName}`}
                  people={people}
                  value={employeeId}
                  onChange={(id, text) => { setEmployeeId(id); setSearching(id === null && text.trim() !== ''); }}
                />
                <div className="meta" style={{ marginTop: 4, marginBottom: 8 }}>
                  Needed only for a Team Member, so the app knows whose scorecard is theirs. Clear the box to unlink.
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                  <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                  Active — an inactive person cannot sign in
                </label>

                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn--primary btn--sm" disabled={searching}
                    onClick={() => onSave(name, employeeId, active)}>Save details</button>
                  <button className="btn btn--sm btn--reject" onClick={onRemove}>Remove person</button>
                </div>
                {searching && (
                  <div className="meta" style={{ marginTop: 6 }}>
                    Choose someone from the list, or clear the employee box, before saving.
                  </div>
                )}
              </div>

              <div style={{ minWidth: 420, flex: 1 }}>
                <div className="section-label" style={{ marginBottom: 8 }}>ACCESS</div>
                {APPS.map((app) => (
                  <div key={app} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--blue-deep)', marginBottom: 4 }}>
                      {APP_LABELS[app]}
                    </div>
                    {inApp(app).length === 0 && <div className="meta">No access</div>}
                    {inApp(app).map((a) => (
                      <div key={a.assignmentId} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 12.5, minWidth: 260 }}>
                          <strong>{a.roleName}</strong> · {locationName(a.locationCode, a.app)}{a.title ? ` · ${a.title}` : ''}
                        </span>
                        <button className="btn btn--sm btn--reject" onClick={() => onRemoveAssignment(a)}>Remove</button>
                      </div>
                    ))}
                  </div>
                ))}

                <div className="section-label" style={{ margin: '14px 0 6px' }}>ADD ACCESS</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div>
                    <label className="field-label" htmlFor={`a-${user.userPrincipalName}`}>APP</label>
                    <select id={`a-${user.userPrincipalName}`} className="control" value={line.app}
                      onChange={(e) => setLine({ ...line, app: e.target.value as AppKey, roleKey: '', locationCode: '' })}>
                      {APPS.map((app) => <option key={app} value={app}>{APP_LABELS[app]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label" htmlFor={`r-${user.userPrincipalName}`}>ROLE</label>
                    <select id={`r-${user.userPrincipalName}`} className="control" value={line.roleKey}
                      onChange={(e) => setLine({ ...line, roleKey: e.target.value })}>
                      <option value="">Choose a role…</option>
                      {roles.filter((r) => r.app === line.app).map((r) => (
                        <option key={r.roleKey} value={r.roleKey}>{r.displayName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="field-label" htmlFor={`l-${user.userPrincipalName}`}>{scopesFor(line.app).label}</label>
                    <select id={`l-${user.userPrincipalName}`} className="control" value={line.locationCode}
                      onChange={(e) => setLine({ ...line, locationCode: e.target.value })}>
                      <option value="">{scopesFor(line.app).allLabel}</option>
                      {scopesFor(line.app).options.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label" htmlFor={`t-${user.userPrincipalName}`}>TITLE (OPTIONAL)</label>
                    <input id={`t-${user.userPrincipalName}`} className="control" placeholder="Laborer"
                      value={line.title} onChange={(e) => setLine({ ...line, title: e.target.value })} />
                  </div>
                  <button className="btn btn--sm" disabled={!line.roleKey}
                    onClick={() => {
                      onAddAssignment(line.roleKey, line.locationCode, line.title);
                      setLine({ ...line, roleKey: '', title: '' });
                    }}>
                    Add access
                  </button>
                </div>
                <div className="meta" style={{ marginTop: 8 }}>
                  A role applies only where it is assigned. For two plants or two regions, add it twice.
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
